// @ts-check
import {
  airwayTypeLookup,
  getFieldLabelMapForAirwayType,
} from '../airway-types'
import { createCrudProp } from '../utils/crud-prop'
import { createSelector } from '../utils/selector-utils'

const awProps = createCrudProp({
  rootKey: 'aw',
  requiredProps: ['type'],
  mapFn: airway => {
    const { name, ...rest } = airwayTypeLookup[airway.type]
    delete rest.fieldMap
    return {
      ...rest,
      ...airway,
      airwayTypeName: name,
    }
  },
  sortFn: (a, b) => {
    const timeA = a.timePlaced
    const timeB = b.timePlaced
    // those without a timePlaced should be at the end
    if (!timeA && timeB) return 1
    if (!timeB && timeA) return -1
    return timeA - timeB
  },
})

/**
 * @param {AirwayType} type The airway type
 * @param {Omit<CaseDataAirwayEntryRaw, 'type'>} [otherProps] Other properties
 *   to set on the airway
 * @returns {any} The changes to apply to the case.
 */
export const actionAddAirway = (type, otherProps = {}) =>
  awProps.actionAdd({ type, ...otherProps })

/**
 * @param {string} id The airway ID
 * @returns {any} The changes to apply to the case.
 */
export const actionRemoveAirway = awProps.actionRemove

/**
 * @param {string} id The airway ID
 * @param {Omit<CaseDataAirwayEntryRaw, 'type'>} updates The updates to apply to
 *   the airway
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateAirway = (id, updates) =>
  awProps.actionUpdate(id, updates)

/** @type {CaseSelector<CaseDataAirwayEntry[]>} */
export const selectAirways = awProps.select

/** @type {CaseSelector<{ [id: string]: CaseDataAirwayEntry }>} */
export const selectAirwaysById = awProps.selectById

/** @type {CaseSelector<AirwayEvent[]>} */
export const selectAirwayEvents = createSelector(selectAirways, airways => {
  const placedAirways = airways.filter(entry => entry.timePlaced)
  /** @type {AirwayEvent[]} */
  const toReturn = []
  for (const airway of placedAirways) {
    const name = airway.name || airway.airwayTypeName
    toReturn.push({
      id: airway.id + '_placed',
      note: `${name} Placed`,
      type: 'airwayPlaced',
      time: airway.timePlaced,
      derived: true,
      showInEventReport: true,
    })
    if (airway.timeRemoved) {
      toReturn.push({
        id: airway.id + '_removed',
        note: `${name} Removed`,
        type: 'airwayRemoved',
        time: airway.timeRemoved,
        derived: true,
        showInEventReport: true,
      })
    }
  }
  return toReturn
})

/** @type {CaseSelector<Record<string, number>>} */
export const selectAirwayTimesByEventId = createSelector(
  selectAirwayEvents,
  events => {
    const toReturn = {}
    for (const event of events) {
      toReturn[event.id] = event.time
    }
    return toReturn
  }
)

/** @type {CaseSelector<AirwayReportEntry[]>} */
export const selectAirwayReport = createSelector(
  selectAirways,
  /**
   * @param {CaseDataAirwayEntry[]} airways
   * @returns {AirwayReportEntry[]}
   */
  airways =>
    airways.map(entry => {
      const { type, id, ...rest } = entry
      const def = airwayTypeLookup[type]
      const name = rest.name || (def && def.name)
      /** @type {AirwayReportEntry} */
      const toReturn = {
        id,
        name,
        type,
        items: [],
        timePlaced: null,
        timeRemoved: null,
      }
      // get a copy so we can delete items if needed
      const labelMapForType = { ...getFieldLabelMapForAirwayType(type) }

      // the complication details fields should not be shown unless the
      // complication field is true
      if (rest.placementComp !== true) {
        delete labelMapForType.placementCompDetail
      }
      if (rest.removalComp !== true) {
        delete labelMapForType.removalCompDetail
      }

      for (const [name, label] of Object.entries(labelMapForType)) {
        let value = rest[name]
        if (value === undefined || name === 'name') {
          continue
        }
        if (name === 'timePlaced' || name === 'timeRemoved') {
          toReturn[name] = value
          continue
        }
        if (name === 'removalComp' || name === 'placementComp') {
          if (value === false) {
            toReturn.items.push([label, 'none'])
          }
          continue
        }
        const valueType = typeof value
        if (valueType === 'boolean') {
          value = value ? 'yes' : 'no'
        }

        toReturn.items.push([label, value])
      }

      return toReturn
    })
)
